import React, { memo, useCallback } from 'react';
import Select from '../../../../components/select';
import Input from '../../../../components/input';
import Button from '../../../../components/button';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useTranslation } from 'react-i18next';
import { Option } from '../../../../types';
import { USER_STATUS_OPTIONS, USER_STATE_OPTIONS } from '../../../../constants';
import { UserManagementProps } from '../../api/types';
import {
    StyledFormControl,
    StyledSelectsWrapper,
    TableFiltersFormControl,
} from '../../../../components/table/components/styled';
import { Box } from '@mui/material';
import { Product } from '../../../products/api/types';
import Multiselect from '../../../../components/multiselect';
import CSVUsersUpload from '../csv-users-upload';
import { useGetTenantProductsQuery } from '../../../../features/products/api/productsApi';

interface TableFiltersProps {
    filters: UserManagementProps;
    updateFilters: (name: string, value: Option) => void;
    mainAction: () => void;
    refetchData: () => void;
}

const TableFilters: React.FC<TableFiltersProps> = ({ filters, updateFilters, mainAction, refetchData }) => {
    const { t } = useTranslation();
    const { status, drugShortNames, state, name } = filters;

    const { data: products } = useGetTenantProductsQuery();

    const onChange = useCallback(
        (name: string, value: Option) => {
            updateFilters(name, value ?? '');
        },
        [updateFilters]
    );

    const handleProductChange = (event: React.ChangeEvent<HTMLInputElement>, item: Product) => {
        const newValue = event.target.checked
            ? [...drugShortNames, item.shortName]
            : drugShortNames.filter((drug: string) => drug !== item.shortName);

        updateFilters('drugShortNames', newValue);
    };

    const transformedProductItems = products
        ? drugShortNames
              .map((drug) => products.find((opt) => opt.shortName === drug))
              .filter((product): product is Product => product !== undefined)
        : [];

    const selectStyles = {
        width: '220px',
        '@media (max-width: 1645px)': {
            width: '50%',
        },
    };

    return (
        <Box>
            <Box
                sx={{
                    float: 'right',
                    margin: '8px 16px',
                    lineHeight: '20px',
                    fontSize: '16px',
                    '@media (max-width: 580px)': {
                        width: 'calc(100% - 32px)',
                        margin: '16px 16px 8px;',
                        '& .MuiButton-root': {
                            width: '100%'
                        },
                    },

                }}
            >
                <CSVUsersUpload
                    sx={{
                        marginRight: '10px',
                        '@media (max-width: 580px)': {
                            marginRight: 0,
                            marginBottom: '10px'
                        },
                    }}
                    refetchData={refetchData}
                />
                <Button
                    icon={AddCircleOutlineIcon}
                    size='large'
                    label={t('invite-user')}
                    sx={{
                        width: '230px',
                    }}
                    onClick={mainAction}
                />
            </Box>

            <StyledSelectsWrapper
                sx={{
                    '@media (max-width: 1645px)': {
                        clear: 'both',
                    },
                }}
            >
                <StyledFormControl sx={{ ...selectStyles, width: '260px' }}>
                    <Input
                        value={name}
                        name='name'
                        onChange={onChange}
                        placeholder={t('first-last-name-placeholder')}
                    />
                </StyledFormControl>
                <StyledFormControl sx={selectStyles}>
                    <Select
                        value={status}
                        name='status'
                        onChange={onChange}
                        label={t('status')}
                        options={USER_STATUS_OPTIONS}
                    />
                </StyledFormControl>
                <StyledFormControl sx={selectStyles}>
                    <Select
                        value={state}
                        name='state'
                        onChange={onChange}
                        label={t('account-state')}
                        options={USER_STATE_OPTIONS}
                    />
                </StyledFormControl>
                <TableFiltersFormControl sx={selectStyles}>
                    {products && (
                        <Multiselect<Product>
                            selectedItems={transformedProductItems}
                            items={products}
                            fetchMoreData={() => null}
                            renderLabel={(product) => `${product.shortName}`}
                            itemIdentifier={(product) => `${product.shortName}`}
                            handleItemChange={handleProductChange}
                            label={t('drug-name')}
                            id={'drugsScrollableDiv'}
                            clearAll={() => updateFilters('drugShortNames', [])}
                        />
                    )}
                </TableFiltersFormControl>
            </StyledSelectsWrapper>
        </Box>
    );
};

export default memo(TableFilters);
